import { Autocomplete, TextField } from '@mui/material'
import { ReactNode, useEffect, useState } from 'react'

interface Props<T> {
  options: T[]
  dataCy?: string
  label?: string
  size?: 'small' | 'medium'
  value?: T[]
  required?: boolean
  error?: boolean
  helperText?: ReactNode
  entityKey?: keyof T
  handleChange: (value?: T[]) => void
  getRangeDisplayValue?: (values: T[]) => string
  getOptionLabel?: (option: T) => string
  sortFn: (val: T[]) => T[]
  validate?: () => void
}

export const RangeDropdown = <T,>({
  options,
  dataCy,
  label,
  size,
  value,
  required,
  error,
  helperText,
  entityKey,
  handleChange,
  getRangeDisplayValue,
  getOptionLabel,
  sortFn,
  validate,
}: Props<T>) => {
  const [fieldValues, setFieldValues] = useState<T[]>([])

  useEffect(() => {
    if (value) setFieldValues(value)
  }, [value])

  const handleValueChange = (values?: T[] | null) => {
    if (!values || values.length === 0) {
      setFieldValues([])
      handleChange()
      return
    }

    let newValue

    if (values && values.length > fieldValues.length) {
      const sortedValues = sortFn(values)

      const minValue = sortedValues[0]
      const maxValue = sortedValues[values.length - 1]

      const startIndex = options.indexOf(minValue)
      const endIndex = options.indexOf(maxValue) + 1

      newValue = options.slice(startIndex, endIndex)
    } else {
      const sortedValues = sortFn(fieldValues)

      const minValue = sortedValues[0]
      const maxValue = sortedValues[fieldValues.length - 1]

      newValue = values.includes(minValue) ? [minValue] : [maxValue]
    }

    setFieldValues(newValue)
    handleChange(newValue)
  }

  const getRangeDisabledOptions = (option: T) => {
    if (fieldValues.length <= 1) return false

    const sortedValues = sortFn(fieldValues)
    const minValue = sortedValues[0]
    const maxValue = sortedValues[fieldValues.length - 1]

    return option !== minValue && option !== maxValue
  }

  return (
    <Autocomplete
      multiple
      fullWidth
      data-cy={dataCy}
      options={sortFn(options)}
      value={fieldValues}
      size={size ?? 'medium'}
      renderTags={getRangeDisplayValue}
      renderInput={params => (
        <TextField
          {...params}
          required={required}
          label={label}
          error={error}
          helperText={helperText}
        />
      )}
      getOptionDisabled={getRangeDisabledOptions}
      onChange={(e, value) => handleValueChange(value)}
      getOptionLabel={entity => (getOptionLabel ? getOptionLabel(entity) : `${entity}`)}
      isOptionEqualToValue={(option: T, value: T) =>
        entityKey ? option[entityKey] === value[entityKey] : option === value
      }
      onBlur={validate}
    />
  )
}
